import throttle from 'lodash/debounce';
import {
  isHtmlElement,
  isFunction,
  isUndefined,
  isDefined,
} from '../scripts/utils/helper';
import DomHelper from '../scripts/utils/dom-helper';

const getStyleComputedProperty = (element, property) => {
  if (element === window) {
    element = document.documentElement;
  }

  if (element.nodeType !== 1) {
    return [];
  }
  // NOTE: 1 DOM access here
  const css = window.getComputedStyle(element, null);
  return property ? css[property] : css;
};

const entries = obj => Object.keys(obj || {})
  .map(key => ([key, obj[key]]));

const getPositionSize = function(el, prop) {
  return el === window || el === document
    ? document.documentElement[prop]
    : el[prop];
};

const getOffsetHeight = el => getPositionSize(el, 'offsetHeight');

const getClientHeight = el => getPositionSize(el, 'clientHeight');

const scope = 'TenInfiniteScroll';
const attributes = {
  /**
   * 节流时延，单位为ms
   */
  delay: {
    type: Number,
    default: 200,
  },
  /**
   * 触发加载的距离阈值，单位为px
   */
  distance: {
    type: Number,
    default: 0,
  },
  /**
   * 是否禁用
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * 是否立即执行加载方法，以防初始状态下内容无法撑满容器。
   */
  immediate: {
    type: Boolean,
    default: true,
  },
};

const getScrollOptions = (el, vm) => {
  if (!isHtmlElement(el)) return {};

  return entries(attributes).reduce((map, [key, option]) => {
    const { type, default: defaultValue } = option;
    let value = el.getAttribute(`infinite-scroll-${key}`);
    value = isUndefined(vm[value]) ? value : vm[value];
    switch (type) {
      case Number:
        value = Number(value);
        value = Number.isNaN(value) ? defaultValue : value;
        break;
      case Boolean:
        // eslint-disable-next-line
        value = isDefined(value) ? value === 'false' ? false : Boolean(value) : defaultValue;
        break;
      default:
        value = type(value);
    }
    map[key] = value;
    return map;
  }, {});
};

const getElementTop = el => el.getBoundingClientRect().top;

const handleScroll = function(cb) {
  const { el, vm, container, observer } = this[scope];
  const { distance, disabled } = getScrollOptions(el, vm);

  if (disabled) return;

  const containerInfo = container.getBoundingClientRect();
  if (!containerInfo.width && !containerInfo.height) return;

  let shouldTrigger = false;

  if (container === el) {
    // be aware of difference between clientHeight & offsetHeight & window.getComputedStyle().height
    const scrollBottom = container.scrollTop + getClientHeight(container);
    shouldTrigger = container.scrollHeight - scrollBottom <= distance;
  } else {
    const heightBelowTop = getOffsetHeight(el) + getElementTop(el) - getElementTop(container);
    const offsetHeight = getOffsetHeight(container);
    const borderBottom = Number.parseFloat(getStyleComputedProperty(container, 'borderBottomWidth'));
    shouldTrigger = heightBelowTop - offsetHeight + borderBottom <= distance;
  }

  if (shouldTrigger && isFunction(cb)) {
    cb.call(vm);
  } else if (observer) {
    observer.disconnect();
    this[scope].observer = null;
  }
};

export default {
  name: 'infinite-scroll',
  // 此处的 props 仅用于生成文档，需要跟 attributes 属性同步更新
  props: {
    /**
     * 节流时延，单位为ms
     */
    delay: {
      type: Number,
      default: 200,
    },
    /**
     * 触发加载的距离阈值，单位为px
     */
    distance: {
      type: Number,
      default: 0,
    },
    /**
     * 是否禁用
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否立即执行加载方法，以防初始状态下内容无法撑满容器。
     */
    immediate: {
      type: Boolean,
      default: true,
    },
  },
  inserted(el, binding, vnode) {
    const cb = binding.value;

    const vm = vnode.context;
    // only include vertical scroll
    const container = DomHelper.getScrollContainer(el, true);
    const { delay, immediate } = getScrollOptions(el, vm);
    const onScroll = throttle(handleScroll.bind(el, cb), delay);

    el[scope] = { el, vm, container, onScroll };

    if (container) {
      container.addEventListener('scroll', onScroll);

      if (immediate) {
        el[scope].observer = new MutationObserver(onScroll);
        const { observer } = el[scope];
        observer.observe(container, { childList: true, subtree: true });
        onScroll();
      }
    }
  },
  unbind(el) {
    const { container, onScroll } = el[scope];
    if (container) {
      container.removeEventListener('scroll', onScroll);
    }
  },
};
